<template>
  <div
    v-if="pending"
    class="bg-white border border-red-500 rounded-lg p-4 text-sm font-proxima"
    role="alertdialog"
    aria-label="Confirm assistant action"
  >
    <template v-if="spec">
      <p class="font-semibold text-gray-700">Confirm: {{ title }}</p>
      <p class="mt-1 text-gray-600">SAMMI will do this only if you click {{ confirmLabel }}.</p>

      <ul
        v-if="sideEffects.length"
        class="mt-2 space-y-1 rounded-md bg-yellow-50 border border-yellow-300 p-2 text-gray-700"
      >
        <li v-for="(effect, i) in sideEffects" :key="i">{{ effect }}</li>
      </ul>

      <dl class="mt-2 space-y-1 text-gray-600">
        <div v-for="(row, i) in fields" :key="i" class="flex gap-2">
          <dt class="font-semibold flex-shrink-0">{{ row.label }}:</dt>
          <dd class="break-words min-w-0 whitespace-pre-wrap">{{ row.value }}</dd>
        </div>
      </dl>
    </template>
    <template v-else>
      <p class="font-semibold text-gray-700">Confirm action</p>
      <p class="mt-1 text-gray-600">
        SAMMI wants to run <span class="font-semibold">{{ toolLabel }}</span> with:
      </p>
      <dl class="mt-2 space-y-1 text-gray-600">
        <div v-for="(value, key) in inputEntries" :key="key" class="flex gap-2">
          <dt class="font-semibold">{{ formatKey(String(key)) }}:</dt>
          <dd class="break-all">{{ formatValue(value) }}</dd>
        </div>
        <div v-if="Object.keys(inputEntries).length === 0" class="italic text-gray-400">No parameters</div>
      </dl>
    </template>

    <!-- The email exactly as the API renders it. Sandboxed: no scripts, no same-origin, no navigation. -->
    <div v-if="emailPreview" class="mt-3">
      <p class="font-semibold text-gray-700 mb-1">Preview</p>
      <iframe
        :srcdoc="emailPreview.html"
        sandbox=""
        referrerpolicy="no-referrer"
        title="Email preview"
        class="w-full h-64 border border-gray-300 rounded-md bg-white"
      />
    </div>

    <!-- Test-only recipient override: typed by the user, never by SAMMI. Shown only when the server allows it. -->
    <div v-if="overrideAllowed" class="mt-3">
      <label for="sammi-dev-recipient" class="block font-semibold text-gray-700">Send to (test servers only)</label>
      <input
        id="sammi-dev-recipient"
        v-model="devRecipient"
        type="email"
        autocomplete="off"
        placeholder="Leave empty to send to your own address"
        class="mt-1 w-full rounded-md border border-gray-400 px-2 py-1 text-sm"
      />
      <p class="mt-1 text-xs text-gray-500">
        Allowed domains: {{ allowedDomains.join(', ') || 'set by the server' }}. The subject gets a [TEST] prefix.
      </p>
      <p v-if="devRecipientError" class="mt-1 text-xs text-red-500">{{ devRecipientError }}</p>
      <p v-else-if="devRecipient.trim()" class="mt-1 text-xs font-semibold text-gray-700">
        This test report goes to {{ devRecipient.trim() }} instead of your own address.
      </p>
    </div>

    <div class="mt-4 flex justify-end gap-3">
      <button
        type="button"
        class="font-proxima text-black text-sm font-semibold px-6 py-2 rounded-md capitalize border border-gray-600 disabled:opacity-50"
        :disabled="resolving"
        @click="onDecline"
      >
        Cancel
      </button>
      <button
        type="button"
        class="font-proxima text-white text-sm font-semibold px-6 py-2 rounded-md capitalize bg-red-500 border border-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
        :disabled="resolving || !!devRecipientError"
        @click="onConfirm"
      >
        {{ confirmLabel }}
      </button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { devRecipientProblem } from '#shared/agent/email-tool';

const agentStore = useAgentStore();

const pending = computed<any>(() => agentStore.pendingConfirmation);
const prepared = computed<any>(() => pending.value?.prepared ?? {});
const spec = computed<any>(() => pending.value?.def?.confirm ?? null);
const input = computed<Record<string, unknown>>(() => {
  const value = pending.value?.toolCall?.input;
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
});

function safeCall<T>(fn: (() => T) | undefined, fallback: T): T {
  if (!fn) return fallback;
  try {
    return fn();
  } catch {
    return fallback;
  }
}

const toolLabel = computed(() => {
  const name = pending.value?.toolCall?.toolName ?? pending.value?.def?.name ?? 'this action';
  return String(name).replace(/_/g, ' ');
});

const title = computed(
  () =>
    safeCall(spec.value?.title ? () => spec.value.title(input.value, prepared.value) : undefined, toolLabel.value) ||
    toolLabel.value
);
const confirmLabel = computed(() => String(spec.value?.confirmLabel ?? 'Confirm'));
const sideEffects = computed<string[]>(() =>
  safeCall(
    spec.value?.sideEffects ? () => spec.value.sideEffects(input.value, prepared.value) : undefined,
    [] as string[]
  ).filter(Boolean)
);
const fields = computed<Array<{ label: string; value: string }>>(() =>
  safeCall(spec.value?.fields ? () => spec.value.fields(input.value, prepared.value) : undefined, [])
);

const inputEntries = input;

const formatKey = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase();

const formatValue = (value: unknown) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const isEmailTool = computed(() => pending.value?.def?.name === 'email_me_report');
const emailPreview = computed(() => (isEmailTool.value ? prepared.value?.emailPreview ?? null : null));
const overrideAllowed = computed(() => isEmailTool.value && prepared.value?.emailOverride?.allowed === true);
const allowedDomains = computed<string[]>(() => prepared.value?.emailOverride?.allowedDomains ?? []);

const devRecipient = ref('');
watch(pending, () => {
  devRecipient.value = '';
});
const devRecipientError = computed(() =>
  overrideAllowed.value ? devRecipientProblem(devRecipient.value, allowedDomains.value) : null
);

const resolving = ref(false);

const onConfirm = async () => {
  if (devRecipientError.value) return;
  resolving.value = true;
  try {
    const typed = devRecipient.value.trim();
    await agentStore.confirmPending(overrideAllowed.value && typed ? { devRecipient: typed } : undefined);
  } finally {
    resolving.value = false;
  }
};

const onDecline = async () => {
  resolving.value = true;
  try {
    await agentStore.declinePending();
  } finally {
    resolving.value = false;
  }
};
</script>
